// React Query hooks for the contract activity log. Two entry points:
//   useRecentContractActivity — useQuery for the last N entries (inline section).
//   useContractActivity       — useInfiniteQuery for drawer with full history + filter.

import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { activityApi } from "../services/api";
import type { ActivityFilter, ActivityPage } from "../types";

const STALE_MS = 30_000;

/** Fetch the N most recent activity entries for the inline section. */
export function useRecentContractActivity(contractId: string, limit = 5) {
  return useQuery<ActivityPage>({
    queryKey: ["activity", contractId, "recent", limit],
    queryFn: () => activityApi.list(contractId, { limit }),
    enabled: !!contractId,
    staleTime: STALE_MS,
  });
}

/** Infinite-query for the full activity drawer with category filter support. */
export function useContractActivity(contractId: string, filter: ActivityFilter) {
  return useInfiniteQuery<ActivityPage>({
    queryKey: ["activity", contractId, filter],
    queryFn: ({ pageParam }) =>
      activityApi.list(contractId, {
        limit: 50,
        eventTypes: filter === "all" ? undefined : filter,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!contractId,
    staleTime: STALE_MS,
  });
}
